
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Database, Activity, Clock, TrendingUp, Shield } from 'lucide-react';

interface TableStats {
  table_name: string;
  row_count: number;
  table_size: string;
  index_size: string;
}

interface ConnectionStats {
  active_connections: number;
  idle_connections: number;
  total_connections: number;
}

export const DatabasePerformanceMonitor = () => {
  const { data: tableStats, isLoading: statsLoading } = useQuery({
    queryKey: ['database-table-stats'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_table_stats');
      if (error) throw error;
      return data as TableStats[];
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const { data: connectionStats, isLoading: connectionsLoading } = useQuery({
    queryKey: ['database-connection-stats'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_connection_stats');
      if (error) {
        // Handle admin access denied gracefully
        if (error.message?.includes('Access denied')) {
          return null;
        }
        throw error;
      }
      return data as ConnectionStats[];
    },
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  if (statsLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Loading Performance Data...
            </CardTitle>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Table Statistics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Table Statistics
            </CardTitle>
            <CardDescription>
              Database table sizes and row counts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {tableStats?.slice(0, 10).map((table) => (
                <div key={table.table_name} className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-sm">{table.table_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {table.row_count.toLocaleString()} rows
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className="text-xs">
                      {table.table_size}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      Indexes: {table.index_size}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Connection Statistics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Connection Pool Status
            </CardTitle>
            <CardDescription>
              Real-time database connection monitoring (Admin only)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {connectionsLoading ? (
              <div className="text-center text-muted-foreground">
                Loading connection stats...
              </div>
            ) : connectionStats === null ? (
              <div className="text-center text-muted-foreground p-4">
                <Shield className="h-8 w-8 mx-auto mb-2 text-orange-500" />
                <p className="text-sm">Admin privileges required to view connection statistics</p>
              </div>
            ) : connectionStats && connectionStats.length > 0 ? (
              <div className="space-y-4">
                {connectionStats.map((conn, index) => {
                  const total = conn.total_connections;
                  const activePercentage = total > 0 ? (conn.active_connections / total) * 100 : 0;
                  const idlePercentage = total > 0 ? (conn.idle_connections / total) * 100 : 0;
                  
                  return (
                    <div key={index} className="space-y-3">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Active Connections</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                              {conn.active_connections}
                            </span>
                            <Badge variant="default" className="text-xs">
                              {activePercentage.toFixed(1)}%
                            </Badge>
                          </div>
                        </div>
                        <Progress value={activePercentage} className="h-2" />
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Idle Connections</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                              {conn.idle_connections}
                            </span>
                            <Badge variant="secondary" className="text-xs">
                              {idlePercentage.toFixed(1)}%
                            </Badge>
                          </div>
                        </div>
                        <Progress value={idlePercentage} className="h-2" />
                      </div>
                      
                      <div className="pt-2 border-t">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Total Connections</span>
                          <Badge variant="outline" className="text-xs">
                            {conn.total_connections}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-muted-foreground">
                No connection data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Performance Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Security & Performance Status
          </CardTitle>
          <CardDescription>
            Current security enhancements and optimization status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-green-500" />
                <span className="font-medium text-sm">Security Enhanced</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Insecure database views removed and replaced with organization-scoped functions
              </p>
            </div>
            
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Database className="h-4 w-4 text-blue-500" />
                <span className="font-medium text-sm">RLS Enforced</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Row Level Security policies ensure users only access their organization's data
              </p>
            </div>
            
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-4 w-4 text-orange-500" />
                <span className="font-medium text-sm">Audit Logging</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Security events are now logged and monitored for suspicious activity
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
